import type React from 'react'
import { type InputHTMLAttributes, useEffect, useState } from 'react'
import { CheckIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import { Text } from './Text'

type CheckboxVariant = 'primary' | 'error'

interface CheckboxStyleProps {
	variant?: CheckboxVariant
	disabled?: boolean
	checked?: boolean
	size?: 'small' | 'medium'
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, CheckboxStyleProps {
	label?: string
	isRequired?: boolean
	labelStyle?: React.CSSProperties
	checkboxStyle?: {
		backgroundColor?: string
		borderColor?: string
		hoverBackgroundColor?: string
		hoverBorderColor?: string
	}
	isAllSelected?: boolean
}

const styles = {
	wrapper: cva('flex items-center gap-2 relative cursor-pointer transition-colors', {
		variants: {
			size: {
				medium: 'text-base',
				small: 'text-sm',
			},
			disabled: {
				true: 'cursor-not-allowed text-neutral-400',
				false: '',
			},
		},
		defaultVariants: {
			size: 'medium',
			disabled: false,
		},
	}),
	input: cva(
		'appearance-none w-5 h-5 border border-neutral-200 bg-neutral-50 hover:bg-neutral-200 rounded transition-colors cursor-pointer',
		{
			variants: {
				size: {
					medium: 'w-5 h-5',
					small: 'w-4 h-4',
				},
				variant: {
					primary: '',
					error: '',
				},
				checked: {
					true: 'bg-primary-600 border-primary-600 hover:bg-primary-800 hover:border-primary-800',
					false: '',
				},
				disabled: {
					true: 'bg-neutral-100 border-neutral-200 hover:bg-neutral-100 cursor-not-allowed',
					false: '',
				},
			},
			defaultVariants: {
				variant: 'primary',
				checked: false,
				disabled: false,
				size: 'medium',
			},
		},
	),
	label: cva('select-none', {
		variants: {
			disabled: {
				true: 'text-neutral-400',
				false: 'text-neutral-800',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	}),
}

export const Checkbox: React.FC<CheckboxProps> = ({
	className,
	size = 'medium',
	variant = 'primary',
	disabled = false,
	label,
	checked: controlledChecked,
	defaultChecked,
	onChange,
	isRequired = false,
	labelStyle,
	checkboxStyle,
	isAllSelected,
	...props
}) => {
	const [internalChecked, setInternalChecked] = useState<boolean>(controlledChecked ?? defaultChecked ?? false)

	// Sync internal state with controlled prop when it changes
	useEffect(() => {
		if (controlledChecked !== undefined) {
			setInternalChecked(controlledChecked)
		}
	}, [controlledChecked])

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newChecked = e.target.checked
		setInternalChecked(newChecked)
		onChange?.(e)
	}

	const isChecked = controlledChecked ?? internalChecked

	const styleProps: CheckboxStyleProps = {
		variant,
		disabled,
		checked: isChecked,
		size,
	}

	const customCheckboxStyle =
		isChecked && checkboxStyle
			? {
					backgroundColor: checkboxStyle.backgroundColor,
					borderColor: checkboxStyle.borderColor,
					'&:hover': {
						backgroundColor: checkboxStyle.hoverBackgroundColor || checkboxStyle.backgroundColor,
						borderColor: checkboxStyle.hoverBorderColor || checkboxStyle.borderColor,
					},
				}
			: undefined

	return (
		<label className={cn(styles.wrapper({ size, disabled }), className)}>
			<input
				type='checkbox'
				className={styles.input(styleProps)}
				disabled={disabled}
				checked={isChecked}
				onChange={handleChange}
				required={isRequired}
				style={customCheckboxStyle}
				{...props}
			/>
			{isChecked && isAllSelected === false && (
				<Text variant='none' weight='semibold' className='absolute left-1/2 -translate-x-1/2 text-neutral-0'>
					-
				</Text>
			)}
			{isChecked && isAllSelected === true && (
				<CheckIcon size={size === 'medium' ? 20 : 16} className='absolute fill-neutral-0' />
			)}
			{isChecked && isAllSelected == null && (
				<CheckIcon size={size === 'medium' ? 20 : 16} className='absolute fill-neutral-0' />
			)}
			{label && (
				<Text variant='none' className={styles.label({ disabled })} style={labelStyle}>
					{`${label}${isRequired ? ' *' : ''}`}
				</Text>
			)}
		</label>
	)
}
